import { useEffect, useState, useMemo } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Clock, Check, Search } from "lucide-react";
import SEO from "@/components/SEO";
import BrandIcon from "@/components/BrandIcon";
import RateCalculator from "@/components/RateCalculator";
import { api } from "@/lib/api";
import { BRAND } from "@/brand";

// Easing for premium motion
const EASE = [0.2, 0.7, 0.2, 1];

/**
 * Programmatic SEO categories. Each entry powers:
 *  - URL slug (/cards/:slug)
 *  - Backend category filter
 *  - Unique <title>, meta description, copy and FAQ blurbs for organic search.
 *
 * Keep copy grounded — Google Ads compliance: no fake stats, no inflated promises.
 */
export const CATEGORIES = {
  retail: {
    slug: "retail",
    label: "Retail",
    apiCategory: "Retail",
    eyebrow: "Retail gift cards",
    h1: ["Sell ", "retail gift cards", "for cash."],
    intro:
      "Trade your unused retail gift cards from major chains for a payout to PayPal, Cash App, Zelle, or Chime. Each card is reviewed by a person before approval.",
    metaTitle: "Sell retail gift cards for cash",
    metaDescription:
      "Sell unused retail gift cards from major US chains. Published rates, human verification, and your choice of payout rail.",
    bullets: [
      "Brands across big-box, grocery and home improvement",
      "Published rate up to the rate on each brand page",
      "No fees, no signup required",
    ],
    faq: [
      {
        q: "Which retail gift cards do you accept?",
        a: "We accept gift cards from major US retail chains across big-box, home improvement, electronics, and e-commerce. See the brand list on this page for the current accepted set.",
      },
      {
        q: "How is my retail card's rate decided?",
        a: "Each brand has a published rate, set by demand and resale liquidity. The exact payout shown on submission is locked once we verify your balance.",
      },
    ],
  },
  tech: {
    slug: "tech",
    label: "Tech",
    apiCategory: "Tech",
    eyebrow: "Tech & streaming gift cards",
    h1: ["Sell ", "tech gift cards", "for cash."],
    intro:
      "From app store credit to streaming subscriptions, turn your unused tech gift cards into a payout. Choose your rail, watch the status, get paid once approved.",
    metaTitle: "Sell tech & streaming gift cards",
    metaDescription:
      "Trade Apple, Google Play, Netflix, Spotify and other tech gift cards for cash. Choose PayPal, Cash App, Zelle, or Chime.",
    bullets: [
      "App store, streaming and platform credit",
      "Same flow whether you have one card or several",
      "Image and number verification before approval",
    ],
    faq: [
      {
        q: "Do you accept partially-used tech cards?",
        a: "Yes, as long as the remaining balance is above the brand's listed minimum and the card hasn't already been redeemed.",
      },
      {
        q: "What proves the card balance?",
        a: "We verify the balance directly with the issuer or by scanning the card image and number you submit.",
      },
    ],
  },
  gaming: {
    slug: "gaming",
    label: "Gaming",
    apiCategory: "Gaming",
    eyebrow: "Gaming gift cards",
    h1: ["Sell ", "gaming gift cards", "for cash."],
    intro:
      "PlayStation, Xbox, Steam, Nintendo — convert unused gaming credit into a payout. Submit your card, our team verifies it, and your cash is sent to the rail you choose.",
    metaTitle: "Sell gaming gift cards for cash",
    metaDescription:
      "Sell PlayStation, Xbox, Steam, and Nintendo gift cards. Published rates and human verification on every submission.",
    bullets: [
      "Console and PC gaming credit accepted",
      "Cards verified by a person, not just an algorithm",
      "Choose any of four payout rails",
    ],
    faq: [
      {
        q: "Are region-locked cards accepted?",
        a: "We currently focus on US-region cards. If your card is region-locked elsewhere it may be rejected — there is no fee if that happens.",
      },
      {
        q: "Can I sell a code that was emailed to me?",
        a: "Yes. Submit a screenshot of the email along with the code. We treat digital and physical cards the same.",
      },
    ],
  },
  food: {
    slug: "food",
    label: "Food & dining",
    apiCategory: "Food",
    eyebrow: "Food & dining gift cards",
    h1: ["Sell ", "food gift cards", "for cash."],
    intro:
      "Coffee, dining and restaurant chain gift cards — convert them into a payout you can use anywhere.",
    metaTitle: "Sell food & restaurant gift cards",
    metaDescription:
      "Sell unused food and dining gift cards. Published rates, human verification, and quick payouts to your chosen rail.",
    bullets: [
      "Coffee chains and restaurant brands",
      "Small balances welcome above each brand's minimum",
      "Payout via PayPal, Cash App, Zelle, or Chime",
    ],
    faq: [
      {
        q: "Is there a minimum balance?",
        a: "Each brand sets its own minimum (typically $10–$25). The submit form shows the live minimum for the brand you pick.",
      },
    ],
  },
  fashion: {
    slug: "fashion",
    label: "Fashion",
    apiCategory: "Fashion",
    eyebrow: "Fashion gift cards",
    h1: ["Sell ", "fashion gift cards", "for cash."],
    intro:
      "Department store and apparel gift cards — converted to cash without resale-app fees or markdowns.",
    metaTitle: "Sell fashion & apparel gift cards",
    metaDescription:
      "Sell department store and apparel gift cards. No signup, human verification, fast payouts.",
    bullets: [
      "Department stores and apparel brands",
      "Pick your payout rail at submission",
      "No platform fees, no listing",
    ],
    faq: [
      {
        q: "Do you accept store credit instead of a gift card?",
        a: "Only physical and digital gift cards are accepted. Store credit tied to a specific account is not transferable.",
      },
    ],
  },
  prepaid: {
    slug: "prepaid",
    label: "Prepaid Visa & Mastercard",
    apiCategory: "Prepaid",
    eyebrow: "Prepaid Visa & Mastercard",
    h1: ["Sell ", "prepaid cards", "for cash."],
    intro:
      "Got a prepaid Visa or Mastercard you don't want to use? Submit it for a payout instead.",
    metaTitle: "Sell prepaid Visa & Mastercard gift cards",
    metaDescription:
      "Trade prepaid Visa and Mastercard gift cards for cash. Human verification, no fees, your choice of payout rail.",
    bullets: [
      "Prepaid Visa and Mastercard accepted",
      "Some issuers require additional verification — we'll let you know",
      "Payout via your chosen rail",
    ],
    faq: [
      {
        q: "Why is the rate on prepaid cards lower?",
        a: "Prepaid Visa and Mastercard cards carry issuer fees and a higher fraud rate, which is priced into our published rate.",
      },
    ],
  },
  beauty: {
    slug: "beauty",
    label: "Beauty",
    apiCategory: "Beauty",
    eyebrow: "Beauty gift cards",
    h1: ["Sell ", "beauty gift cards", "for cash."],
    intro:
      "Beauty and cosmetics retailer gift cards — turn them into a payout via PayPal, Cash App, Zelle, or Chime.",
    metaTitle: "Sell beauty & cosmetics gift cards",
    metaDescription:
      "Sell unused beauty and cosmetics gift cards. No signup required, published rates, fast human review.",
    bullets: [
      "Beauty and cosmetics brands",
      "Small minimums on most brands",
      "Pick your payout rail at submission",
    ],
    faq: [
      {
        q: "Are loyalty rewards accepted?",
        a: "Only standalone gift cards are accepted. Loyalty point balances tied to a specific account cannot be transferred.",
      },
    ],
  },
  travel: {
    slug: "travel",
    label: "Travel",
    apiCategory: "Travel",
    eyebrow: "Travel gift cards",
    h1: ["Sell ", "travel gift cards", "for cash."],
    intro:
      "Travel, rideshare and stay credits — converted to a payout you can spend anywhere.",
    metaTitle: "Sell travel & rideshare gift cards",
    metaDescription:
      "Sell Uber, Airbnb and other travel gift cards. Human review, no fees, fast payout to your chosen rail.",
    bullets: [
      "Rideshare and short-stay credits",
      "Verified balances locked at submission",
      "Choose PayPal, Cash App, Zelle, or Chime",
    ],
    faq: [
      {
        q: "Do you accept airline gift cards?",
        a: "Coverage varies. The Accepted Cards page lists the current set — submit what we accept today, and we'll add more brands over time.",
      },
    ],
  },
};

export default function CategoryPage() {
  const { category } = useParams();
  const cfg = CATEGORIES[category];
  const [brands, setBrands] = useState([]);
  const [q, setQ] = useState("");
  const [otherCats, setOtherCats] = useState([]);

  useEffect(() => {
    if (!cfg) return;
    api
      .get(`/brands?category=${encodeURIComponent(cfg.apiCategory)}&limit=200`)
      .then((r) => setBrands(r.data || []));
    api.get(`/brands?limit=200`).then((r) => {
      const cats = {};
      (r.data || []).forEach((b) => {
        cats[b.category] = (cats[b.category] || 0) + 1;
      });
      setOtherCats(
        Object.entries(cats).map(([apiCat, count]) => ({ apiCat, count }))
      );
    });
  }, [cfg]);

  const filtered = useMemo(
    () =>
      brands.filter((b) => b.name.toLowerCase().includes(q.toLowerCase())),
    [brands, q]
  );

  if (!cfg) return <Navigate to="/accepted-cards" replace />;

  const topRate = brands.reduce(
    (m, b) => Math.max(m, b.rates?.receipt ?? b.rate ?? 0),
    0
  );

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "ItemList",
        name: `${cfg.label} gift cards — ${BRAND.name}`,
        itemListElement: brands.slice(0, 50).map((b, i) => ({
          "@type": "ListItem",
          position: i + 1,
          url: `https://${BRAND.domain}/sell-${b.slug}-gift-card`,
          name: `${b.name} gift card`,
        })),
      },
      {
        "@type": "FAQPage",
        mainEntity: cfg.faq.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  };

  return (
    <>
      <SEO
        title={cfg.metaTitle}
        description={cfg.metaDescription}
        schema={schema}
      />

      {/* ============== HERO ============== */}
      <section
        className="search-home-hero-section"
        data-testid={`category-hero-${cfg.slug}`}
        style={{ paddingBottom: 30 }}
      >
        <div className="main-container w-container">
          <Crumbs label={cfg.label} />

          <div style={{ display: "inline-flex", alignItems: "center", padding: "6px 14px", borderRadius: 90, background: "#eaf0ff", color: "#346af8", fontSize: 12, fontWeight: 700, letterSpacing: ".12em", textTransform: "uppercase", marginTop: 18, marginBottom: 18 }}
            data-testid={`category-eyebrow-${cfg.slug}`}>
            <span>{cfg.eyebrow}</span>
          </div>

          <h1 className="h1 _700-bold" data-testid={`category-h1-${cfg.slug}`} style={{ maxWidth: 900 }}>
            {cfg.h1[0]}
            <span style={{ color: "#346af8" }}>
              {cfg.h1[1]}
            </span>{" "}
            {cfg.h1[2]}
          </h1>

          <div style={{ marginTop: 24, display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 40, alignItems: "end" }} className="cat-hero-grid">
            <p style={{ fontSize: 17, color: "#4a4d5f", lineHeight: 1.6, maxWidth: 680 }}>
              {cfg.intro}
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12, justifyContent: "flex-start" }}>
              <Link
                to="/submit"
                data-testid={`category-cta-submit-${cfg.slug}`}
                className="primary-button w-button"
              >
                Sell a {cfg.label.split(" ")[0].toLowerCase()} card
              </Link>
              <Link
                to="/rates"
                className="btn w-inline-block"
              >
                <div className="btn-text">See rates</div>
              </Link>
            </div>
          </div>

          {brands.length > 0 && (
            <div style={{ marginTop: 40, display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14 }} className="cat-kpi-grid">
              <KPI label="Brands in this category" value={brands.length} />
              <KPI label="Top published rate" value={topRate ? `${topRate}%` : "—"} />
              <KPI label="Fees" value="None" />
              <KPI label="Signup" value="Not needed" />
            </div>
          )}
        </div>
        <style>{`
          @media (max-width: 900px) { .cat-hero-grid { grid-template-columns: 1fr !important; } .cat-kpi-grid { grid-template-columns: repeat(2, 1fr) !important; } }
        `}</style>
      </section>

      {/* ============== BRAND GRID + CALCULATOR ============== */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 pb-16 md:pb-24 pt-14 md:pt-20">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-8">
            <div className="flex items-end justify-between gap-4 mb-5">
              <h2 className="font-display text-2xl md:text-3xl font-bold text-ink tracking-tight">
                {cfg.label} brands we accept
              </h2>
              <div className="relative w-44 md:w-60 hidden sm:block">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-ink" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search"
                  data-testid={`category-search-${cfg.slug}`}
                  className="w-full h-10 pl-9 pr-3 rounded-lg border border-warm focus:outline-none focus:ring-2 focus:ring-emerald-ink/40 text-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {filtered.map((b, i) => (
                <motion.div
                  key={b.id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: i * 0.02, ease: EASE }}
                >
                  <Link
                    to={`/sell-${b.slug}-gift-card`}
                    data-testid={`category-brand-${b.slug}`}
                    className="group h-full flex flex-col bg-white border border-warm rounded-2xl p-4 hover:border-ink hover:-translate-y-0.5 soft-shadow transition-all"
                  >
                    <div className="flex items-center gap-3">
                      <span
                        className="w-11 h-11 rounded-xl text-base flex-shrink-0"
                        style={{ background: b.color }}
                      >
                        <BrandIcon
                          logoKey={b.logo_key}
                          name={b.name}
                          className="w-full h-full"
                        />
                      </span>
                      <div className="min-w-0">
                        <div className="font-display font-semibold text-ink truncate">
                          {b.name}
                        </div>
                        <div className="text-[11px] text-muted-ink">
                          ${b.min}–${b.max}
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#eaf0ff] text-emerald-ink text-[11px] font-semibold font-mono-display self-start">
                      Up to {b.rates?.receipt ?? b.rate}%
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>

            {filtered.length === 0 && brands.length > 0 && (
              <div className="text-center py-12 text-muted-ink">
                No brands match your search.
              </div>
            )}
            {brands.length === 0 && (
              <div className="text-center py-12 text-muted-ink">
                Loading brands…
              </div>
            )}

            {/* Bullets / value strip */}
            <div className="mt-12 grid sm:grid-cols-3 gap-4">
              {cfg.bullets.map((b) => (
                <div
                  key={b}
                  className="flex items-start gap-3 bg-canvas-2 rounded-xl p-4 border border-warm"
                >
                  <span className="w-8 h-8 rounded-lg bg-[#eaf0ff] text-emerald-ink flex items-center justify-center flex-shrink-0">
                    <Check className="w-4 h-4" />
                  </span>
                  <p className="text-sm text-ink-2 leading-snug">{b}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-4 lg:sticky lg:top-24">
            <RateCalculator />
          </div>
        </div>
      </section>

      {/* ============== TRUST STRIP ============== */}
      <section className="bg-canvas-2 border-y border-warm">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-12 grid sm:grid-cols-3 gap-6">
          <TrustItem
            Icon={ShieldCheck}
            t="Human verification"
            d="Each submission is reviewed by a person before approval."
          />
          <TrustItem
            Icon={Clock}
            t="Live status updates"
            d="Track every step from upload to payout by reference number."
          />
          <TrustItem
            Icon={Check}
            t="No surprise fees"
            d="The payout we lock at submission is what you receive."
          />
        </div>
      </section>

      {/* ============== HOW IT WORKS ============== */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 py-16 md:py-24">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-ink font-semibold">
              · How it works
            </div>
            <h2 className="mt-3 font-display text-3xl md:text-4xl font-bold text-ink tracking-tight leading-[1.05]">
              Sell your {cfg.label.toLowerCase()} card in four steps.
            </h2>
            <p className="mt-4 text-ink-2 leading-relaxed">
              The flow is the same for every brand. Pick yours, get a quote,
              verify, and pick how you want to be paid.
            </p>
            <Link
              to="/submit"
              data-testid={`category-howitworks-cta-${cfg.slug}`}
              className="mt-6 inline-flex items-center gap-1.5 h-11 px-5 rounded-lg bg-ink text-white font-semibold hover:opacity-90 transition-all"
            >
              Start a submission <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <ol className="lg:col-span-7 space-y-3">
            {[
              ["01", "Pick your brand & enter balance", "See the live rate and your exact payout."],
              ["02", "Add card details + photo", "Encrypted upload. Reviewed before approval."],
              ["03", "Choose a payout rail", "PayPal, Cash App, Zelle (US), or Chime."],
              ["04", "Get paid once approved", "Track every status by reference number."],
            ].map(([n, t, d]) => (
              <li
                key={n}
                className="flex items-start gap-4 bg-white border border-warm rounded-2xl p-5 soft-shadow"
              >
                <span className="font-mono-display text-emerald-ink text-sm">
                  {n}
                </span>
                <div>
                  <div className="font-display font-semibold text-ink">{t}</div>
                  <div className="text-sm text-ink-2 mt-1">{d}</div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ============== FAQ ============== */}
      <section className="bg-canvas-2 border-t border-warm">
        <div className="max-w-3xl mx-auto px-5 md:px-10 py-16 md:py-24">
          <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-ink font-semibold">
            · {cfg.label} FAQ
          </div>
          <h2 className="mt-3 font-display text-3xl md:text-4xl font-bold text-ink tracking-tight">
            Common questions
          </h2>
          <div className="mt-8 space-y-3">
            {cfg.faq.map((f, i) => (
              <details
                key={i}
                data-testid={`category-faq-${cfg.slug}-${i}`}
                className="group bg-white border border-warm rounded-xl p-5"
              >
                <summary className="cursor-pointer font-display font-semibold text-ink flex items-center justify-between gap-4 list-none">
                  {f.q}
                  <span className="text-muted-ink group-open:rotate-45 transition-transform text-xl leading-none">
                    +
                  </span>
                </summary>
                <p className="mt-3 text-ink-2 leading-relaxed text-sm">
                  {f.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ============== OTHER CATEGORIES ============== */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 py-14 md:py-20">
        <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-ink font-semibold">
          · Browse more
        </div>
        <h2 className="mt-3 font-display text-2xl md:text-3xl font-bold text-ink tracking-tight">
          Other gift card categories
        </h2>
        <div className="mt-6 flex flex-wrap gap-2">
          {Object.values(CATEGORIES)
            .filter((c) => c.slug !== cfg.slug)
            .map((c) => {
              const count =
                otherCats.find((x) => x.apiCat === c.apiCategory)?.count || 0;
              return (
                <Link
                  key={c.slug}
                  to={`/cards/${c.slug}`}
                  data-testid={`category-link-${c.slug}`}
                  className="inline-flex items-center gap-2 h-11 px-4 rounded-full bg-white border border-warm text-ink hover:border-ink transition-all text-sm font-semibold"
                >
                  {c.label}
                  {count > 0 && (
                    <span className="font-mono-display text-[11px] opacity-70">
                      {count}
                    </span>
                  )}
                </Link>
              );
            })}
        </div>
      </section>
    </>
  );
}

function Crumbs({ label }) {
  return (
    <nav
      style={{ fontSize: 11, letterSpacing: ".14em", textTransform: "uppercase", color: "#9a93a6", fontWeight: 700, display: "flex", alignItems: "center", gap: 8 }}
      aria-label="Breadcrumb"
      data-testid="category-breadcrumbs"
    >
      <Link to="/" style={{ color: "#6c6376", textDecoration: "none" }}>Home</Link>
      <span style={{ color: "#d8dcec" }}>/</span>
      <Link to="/accepted-cards" style={{ color: "#6c6376", textDecoration: "none" }}>Cards</Link>
      <span style={{ color: "#d8dcec" }}>/</span>
      <span style={{ color: "#346af8" }}>{label}</span>
    </nav>
  );
}

function KPI({ label, value }) {
  return (
    <div style={{ background: "#fff", border: "1px solid #eaeaf0", borderRadius: 16, padding: 18, boxShadow: "0 6px 16px -12px rgba(41,45,85,.2)" }}>
      <div style={{ fontFamily: '"Hanken Grotesk", sans-serif', fontSize: 24, fontWeight: 800, color: "#0b0d17" }}>{value}</div>
      <div style={{ fontSize: 11, letterSpacing: ".08em", textTransform: "uppercase", color: "#9a93a6", marginTop: 4, fontWeight: 600 }}>
        {label}
      </div>
    </div>
  );
}

function TrustItem({ Icon, t, d }) {
  return (
    <div className="flex items-start gap-3">
      <span className="w-9 h-9 rounded-lg bg-[#eaf0ff] text-emerald-ink flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4" />
      </span>
      <div>
        <div className="font-display font-semibold text-ink">{t}</div>
        <div className="text-sm text-ink-2 leading-relaxed mt-0.5">{d}</div>
      </div>
    </div>
  );
}
